import React from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaTags, FaVideo, FaStream, FaTicketAlt, FaMicrophone, FaMapMarkerAlt, FaChartBar } from 'react-icons/fa';
import { useEffect, useState } from 'react';
import { getEstadisticas } from '../../components/http/Actions/EstadisticasAction';

const AdminHome = () => {
  // Definir las opciones del panel de administrador
  const adminOptions = [
    {
      title: 'Gestor de Usuarios',
      description: 'Administra las cuentas de usuario, permisos y suscripciones',
      icon: <FaUsers className="w-12 h-12" />,
      path: '/admin/usuario',
      color: 'from-blue-500 to-blue-700'
    },
    {
      title: 'Gestor de Comediantes',
      description: 'Administra los perfiles, información y contenido de los comediantes',
      icon: <FaMicrophone className="w-12 h-12" />,
      path: '/admin/comediantes',
      color: 'from-pink-500 to-pink-700'
    },
    {
      title: 'Gestor de Tags',
      description: 'Organiza y administra las etiquetas de categorización de contenido',
      icon: <FaTags className="w-12 h-12" />,
      path: '/admin/tags',
      color: 'from-green-500 to-green-700'
    },
    {
      title: 'Gestor de VideoShows',
      description: 'Sube, edita y administra los videos de shows de comedia',
      icon: <FaVideo className="w-12 h-12" />,
      path: '/admin/videoshows',
      color: 'from-purple-500 to-purple-700'
    },
    {
      title: 'Gestor de Livestreams',
      description: 'Configura y administra eventos en vivo y transmisiones',
      icon: <FaStream className="w-12 h-12" />,
      path: '/admin/livestreams',
      color: 'from-yellow-500 to-yellow-700'
    },
    {
      title: 'Gestor de Entradas',
      description: 'Administra la venta y distribución de entradas para eventos',
      icon: <FaTicketAlt className="w-12 h-12" />,
      path: '/admin/entradas',
      color: 'from-red-500 to-red-700'
    },
    { title: 'Gestor de Lugares',
      description: 'Administra los lugares donde se realizan los eventos',
      icon: <FaMapMarkerAlt className="w-12 h-12" />,
      path: '/admin/lugares',
      color: 'from-teal-500 to-teal-700'
    },
    { title: 'Gestor de Eventos',
      description: 'Crea y administra eventos de comedia, incluyendo fechas y ubicaciones',
      icon: <FaMapMarkerAlt className="w-12 h-12" />,
      path: '/admin/eventos',
      color: 'from-indigo-500 to-indigo-700'
    },
    {
      title: 'Módulo de Reportes',
      description: 'Genera informes detallados de performance y comparativas entre comediantes',
      icon: <FaChartBar className="w-12 h-12" />,
      path: '/admin/reportes',
      color: 'from-orange-500 to-orange-700'
    }
  ];

  // Obtener estadisticas de backend
  const [stats, setStats] = useState({
    usuariosTotales: 0,
    suscripcionesPremium: 0,
    comediantes: 0,
    eventosActivos: 0,
    videosPublicados: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        await getEstadisticas(setStats, setError, token);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Main content */}
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {adminOptions.map((option, index) => (
            <Link 
              to={option.path} 
              key={index}
              className="block"
            >
              <div className={`bg-gradient-to-r ${option.color} rounded-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 h-full`}>
                <div className="p-8 flex flex-col h-full">
                  <div className="text-white mb-4">
                    {option.icon}
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">{option.title}</h2>
                  <p className="text-gray-100 flex-grow mb-4">{option.description}</p>
                  <div className="inline-flex items-center text-white">
                    <span>Acceder</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Estadísticas rápidas */}
        <div className="mt-12 bg-zinc-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-6">Resumen de la Plataforma</h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-10 h-10 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded mb-4">
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-zinc-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Usuarios Totales</p>
                <p className="text-3xl font-bold text-white">{stats.usuariosTotales || 0}</p>
              </div>
              <div className="bg-zinc-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Suscripciones Premium</p>
                <p className="text-3xl font-bold text-white">{stats.suscripcionesPremium || 0}</p>
              </div>
              <div className="bg-zinc-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Comediantes</p>
                <p className="text-3xl font-bold text-white">{stats.comediantes || 0}</p>
              </div>
              <div className="bg-zinc-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Eventos Activos</p>
                <p className="text-3xl font-bold text-white">{stats.eventosActivos || 0}</p>
              </div>
              <div className="bg-zinc-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Videos Publicados</p>
                <p className="text-3xl font-bold text-white">{stats.videosPublicados || 0}</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminHome;